// Login.js
import React, { useState, useRef, useEffect, useContext } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../api/axios";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import "./Login.css";

/* ── Auto-detect country from phone number ── */
function detectCountry(number) {
  const digits = number.replace(/\D/g, "");
  // Indian mobile: 10 digits starting with 6-9
  if (digits.length === 10 && /^[6-9]/.test(digits)) {
    return { country: "IN", code: "+91", bareNumber: digits };
  }
  // Chinese mobile: 11 digits starting with 1
  if (digits.length === 11 && /^1/.test(digits)) {
    return { country: "CN", code: "+86", bareNumber: digits };
  }
  return null;
}

export default function Login() {
  const [input, setInput] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState("IDENTIFIER"); // IDENTIFIER | PASSWORD
  const [openRolePopup, setOpenRolePopup] = useState(false);
  const [inputError, setInputError] = useState("");
  const [countryCode, setCountryCode] = useState("+91");

  const navigate = useNavigate();
  const location = useLocation();
  const { login: contextLogin } = useContext(AuthContext);

  const passwordRef = useRef(null);

  // Auto-focus password field when it appears
  useEffect(() => {
    if (step === "PASSWORD" && passwordRef.current) {
      const timer = setTimeout(() => {
        passwordRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [step]);

  const buildLoginPayload = (value) => {
    const userLocation = localStorage.getItem("user_location")
      ? JSON.parse(localStorage.getItem("user_location"))
      : {};

    const isEmail = value.includes("@");

    let finalUsername = value;
    let email = isEmail ? value : "";
    let mobile = "";

    if (!isEmail) {
      const stripped = value.replace(/^(\+91|\+86)/, "").replace(/\D/g, "");
      if (stripped) {
        finalUsername = `${countryCode}${stripped}`;
        mobile = finalUsername;
      }
    }

    return {
      username: finalUsername,
      email,
      mobile,
      loginLocation: {
        city: userLocation.city || "Unknown City",
        state: userLocation.state || "Unknown State",
        country: userLocation.country || "Unknown Country",
        fullAddress: userLocation.full_address || "Unknown Address",
      },
    };
  };

  const handleLogin = async (e) => {
    if (e) e.preventDefault();

    if (!input.trim()) {
      setInputError("Enter email or mobile number");
      return;
    }

    let normalizedInput = input.trim();

    if (step === "IDENTIFIER") {
      const isEmail = normalizedInput.includes("@");

      if (isEmail) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(normalizedInput)) {
          setInputError("Enter a valid email address or mobile number");
          return;
        }
        normalizedInput = normalizedInput.toLowerCase();
      } else {
        // Reject if input contains non-digit chars
        const digitsOnly = normalizedInput.replace(/^\+/, "");
        if (!/^\d+$/.test(digitsOnly)) {
          setInputError("Enter a valid mobile number");
          return;
        }
        const cleaned = normalizedInput.replace(/\D/g, "");

        // Reject if user typed country code (91 or 86) as part of number
        if (cleaned.startsWith("91") && cleaned.length > 10) {
          setInputError("Enter mobile number without country code");
          return;
        }
        if (cleaned.startsWith("86") && cleaned.length > 11) {
          setInputError("Enter mobile number without country code");
          return;
        }

        // Indian numbers: 10 digits starting with 6-9
        // Chinese numbers: 11 digits starting with 1
        if (!((cleaned.length === 10 && /^[6-9]/.test(cleaned)) ||
              (cleaned.length === 11 && /^1/.test(cleaned)))) {
          setInputError("Enter a valid mobile number");
          return;
        }
        normalizedInput = cleaned;
      }
      setInputError("");
    }

    try {
      if (step === "PASSWORD") {
        const res = await api.post("/auth/verify-password", {
          username: buildLoginPayload(input).username,
          password,
        });

        // Use AuthContext to save complete user object from API response
       contextLogin(
            res.data.user,
            res.data.vendorProfile,
            res.data.agentProfile
          );
        // Store token separately in localStorage for API calls
        localStorage.setItem("token", res.data.token);
        // if (res.data.vendorProfile) {
        //   localStorage.setItem(
        //     "vendorProfile",
        //     JSON.stringify(res.data.vendorProfile)
        //   );
        // }
        navigate(location.state?.redirectTo || "/");
        return;
      }

      const loginPayload = buildLoginPayload(normalizedInput);
      const res = await api.post("/auth/login", loginPayload);

      if (res.data.requiresPassword) {
        setStep("PASSWORD");
        return;
      }

      if (res.data.requiresPasswordSetup) {
        const user = res.data.user;
        user.isRegistration = true;
        // Use AuthContext to save complete user object for registration
        contextLogin(user);
        if (res.data.token) localStorage.setItem("token", res.data.token);
        navigate(`/register/${user.role}`);
        return;
      }

      const user = res.data.user;
      if (res.data.token) {
        try {
          const payload = JSON.parse(atob(res.data.token.split('.')[1]));
          if (payload.registration) user.isRegistration = true;
        } catch (e) { console.error("Failed to decode token:", e.message); }
      }
      // Use AuthContext to save complete user object
      contextLogin(user);
      if (res.data.token) localStorage.setItem("token", res.data.token);
      navigate("/");
    } catch (err) {
      if (err.response?.status === 400) {
        setOpenRolePopup(true);
      } else {
        alert("Login failed. Try again.");
      }
    }
  };

  const selectRole = async (role) => {
    try {
      const res = await api.post("/auth/login", {
        ...buildLoginPayload(input.trim()),
        role,
      });

      const userData = res.data.user;
      if (res.data.isNewUser || res.data.requiresPasswordSetup) userData.isRegistration = true;

      // Use AuthContext to save complete user object
      contextLogin(userData);
      if (res.data.token) localStorage.setItem("token", res.data.token);
      setOpenRolePopup(false);
      navigate(`/register/${role}`);
    } catch (err) {
      alert("Registration failed");
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "flex-start", justifyContent: "center", backgroundColor: "#f3f3f3", pt: 6 }}>
      <Paper elevation={3} sx={{ p: 2.5, width: 400, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>Sign in</Typography>

        <Box component="form" onSubmit={handleLogin} sx={{ width: "100%" }}>
          <TextField
            fullWidth
            label="Mobile number or Email"
            value={input}
            onChange={(e) => {
              const val = e.target.value.toLowerCase();
              // Auto-detect country code from first digit if user is typing digits
              if (val && !val.includes("@")) {
                const firstDigit = val.replace(/\D/g, "")[0];
                if (firstDigit === "1") setCountryCode("+86");
                else if (/^[6-9]$/.test(firstDigit)) setCountryCode("+91");
              }
              setInput(val);
              setInputError("");
              if (step === "PASSWORD") { setStep("IDENTIFIER"); setPassword(""); }
            }}
            InputProps={{
              startAdornment: /^\+?\d+$/.test(input) ? (
                <InputAdornment position="start">
                  <Select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    sx={{
                      fontSize: 14,
                      height: 32,
                      "& .MuiSelect-select": { py: 0, pr: 2 },
                      "& fieldset": { border: "none" },
                    }}
                  >
                    <MenuItem value="+91">+91</MenuItem>
                    <MenuItem value="+86">+86</MenuItem>
                  </Select>
                </InputAdornment>
              ) : undefined,
            }}
            inputProps={{ style: { textTransform: "lowercase" } }}
            error={!!inputError}
            helperText={inputError}
            sx={{ mb: 2 }}
          />

          {step === "PASSWORD" && (
            <>
              <TextField
                fullWidth label="Password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} sx={{ mb: 1 }}
                inputRef={passwordRef}
                onKeyDown={(e) => { if (e.key === "Enter") handleLogin(e); }}
              />
              <Typography sx={{ color: "#0066c0", cursor: "pointer", fontSize: 13, mb: 2 }}
                onClick={() => navigate("/forgot-password", { state: { username: input } })}>
                Forgot Password?
              </Typography>
            </>
          )}

          <Button type="submit" variant="contained" fullWidth
            sx={{ backgroundColor: "#ff9900", color: "#fff", textTransform: "none", fontWeight: 600, borderRadius: "20px", py: 1, "&:hover": { backgroundColor: "#ff8800" } }}>
            {step === "PASSWORD" ? "Sign in" : "Continue"}
          </Button>
        </Box>

        <Typography variant="caption" sx={{ display: "block", mt: 1.2, color: "text.secondary", lineHeight: 1.4 }}>
          By continuing, you agree to our{" "}
          <Box component="span" sx={{ color: "#0066c0", cursor: "pointer" }}>Terms of Use</Box> and{" "}
          <Box component="span" sx={{ color: "#0066c0", cursor: "pointer" }}>Privacy Policy</Box>.
        </Typography>
      </Paper>

      <Dialog open={openRolePopup} onClose={() => setOpenRolePopup(false)}>
        <IconButton onClick={() => setOpenRolePopup(false)} sx={{ position: "absolute", top: 8, right: 8 }}><CloseIcon /></IconButton>
        <DialogTitle>Select Your Role</DialogTitle>
        <DialogContent><Typography>Please choose how you want to continue:</Typography></DialogContent>
        <DialogActions sx={{ flexDirection: "column", p: 2 }}>
          {["customer", "vendor", "agent"].map((r) => (
            <Button key={r} fullWidth variant="contained" onClick={() => selectRole(r)}
              sx={{ mt: 1, backgroundColor: "#ff9900", color: "#fff", textTransform: "none", fontWeight: 600, borderRadius: "20px", "&:hover": { backgroundColor: "#ff8800" } }}>
              I am a {r}
            </Button>
          ))}
        </DialogActions>
      </Dialog>
    </Box>
  );
}